import React, { useState } from 'react';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  Divider,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  SectionHeader,
  Typography,
} from 'web-library';
import { useSelector } from 'react-redux';
import { candidateService } from '../../../../api';

import PersonalDetails from './PersonalDetails';
import Declaration from '../../../../components/Declaration';
import ExamLevelDetails from './ExamLevelDetails';
import AppealFees from './AppealFees';
import { fallbackEmptyString, parseLocationSearch } from '../../../../utils/StringUtils';
import PaymentButton, { PaymentMethod } from '../../../payment/components/PaymentButton';
import { selectBankTransferOption } from '../../../../store/resitSlice';
import RouteKey from '../../../../routes/key';
import { AppealDraftReviewParams } from '../../../../api/candidate/appeal/model';
import FormHeader from '../../../../components/form-items/FormHeader';
import { ApplicationType } from '../../../../api/candidate/config/model';
import LoadingComponent from '../../../../components/LoadingComponent';
import { useGetPaymentAccess } from '../../../../api-refactored/financial/useGetPaymentAccess';

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  errorText: {
    marginTop: theme.spacing(1),
    color: theme.color.state.stateError,
  },
  dialogBold: {
    fontWeight: 'bold',
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  buttonContainer: {
    marginBottom: theme.spacing(7),
  },
  button:{

  },
  examLevelTable: {}
}));

const { receipt, applications } = RouteKey.appeal.child;
const { appeal } = RouteKey.child;

const generateColumns = (t: any): MUIDataTableColumnDef[] => [
  {
    label: t('appeal->>AppealReview.Subject Name'),
    name: 'nameDescription',
  },
  {
    label: t('appeal->>AppealReview.Subject Code'),
    name: 'nameCode',
  },
  {
    label: t('appeal->>AppealReview.Grade Alphabetical'),
    name: 'gradeAlphabetical',
  },
  {
    label: t('appeal->>AppealReview.Grade Numerical'),
    name: 'gradeNumerical',
  },
  {
    label: t('appeal->>AppealReview.Language Medium'),
    name: 'languageMedium',
  },
  {
    name: 'appealFee',
    options: {
      customHeadLabelRender: () => (
        <Grid container justify="flex-end">
          {t('appeal->>AppealReview.Appeal Fees Payable')}
          <br />
          {t('appeal->>AppealReview.(incl GST) (SGD)')}
        </Grid>
      ),
      customBodyRender: (value: string) => (
        <Grid container justify="flex-end">
          {`S$${value}`}
        </Grid>
      ),
    },
  },
];

const AppealReview = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const [errMsg, setErrMsg] = useState<boolean>(false);
  const [isDeclared, setIsDeclared] = useState<boolean>(false);
  const [isPaymentDisabled, setIsPaymentDisabled] = useState(false);
  const bankTransferEnabled = useSelector(selectBankTransferOption);

  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
    Array<PaymentMethod>
  >([]);

  useGetPaymentAccess({
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if ((res.bankTransferMethod && reviewDraftData?.offlinePayment) || bankTransferEnabled) {
        paymentAccessDetailsArray.push(PaymentMethod.BANK_TRANSFER);
      }
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
  });

  const location = useLocation<AppealDraftReviewParams>();
  const { appId = '' } = parseLocationSearch(location.search);

  const { data: reviewDraftData, isLoading } = candidateService.appeal.candidate.useGetReviewDraft(
    appId
  );


  /** to handle subject selection
   * */
  const handleAgreement = () => {
    setErrMsg(false);
    setIsDeclared(!isDeclared);
  };

  /** to handle go back previous page/action
   * */
  const onBack = () => {
    history.goBack();
  };

  /** to save candidate after payment is successful
   * */
  const registerCandidateResponse = candidateService.appeal.candidate.usePostAppealProceedToPayment(
    (res) => {
      if (res.data.paymentSuccess) {
        history.push(
          `/${appeal}/${applications.route}/${receipt.route}?internalTransactionRef=${res.data.internalTransactionRef}`
        );
      }
    },
    () => {
      // do nothing
    }
  );

  const handleClickPaymentButton = (callback: () => void) => {
    if (!isDeclared) {
      setErrMsg(true);
    } else {
      setErrMsg(false);
      callback();
    }
  };

  const handlePaymentOption = (
    callback: (internalTransactionRef: string) => void,
    method?: string
  ) => {
    registerCandidateResponse.mutate(
      {
        applicationId: appId,
        paymentMode: fallbackEmptyString(method),
      },
      {
        onSuccess: (res) => {
          callback(res.data.internalTransactionRef);
        },
        onError: () => {
          callback('');
        },
      }
    );
  };

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <>
      {reviewDraftData && (
        <Box>
          <ContentTemplate isContinuable type="full">
            <SectionHeader title={t('appeal->>ReviewAppeal.Review Application')} />
            <PersonalDetails {...reviewDraftData.candidateDetails} />
            <Divider />
            <ExamLevelDetails
              formHeader={t('appeal->>ReviewAppeal.Selected Subject(s)')}
              {...reviewDraftData.selectedSubjects}
            />
            <Grid sm={12} className={classes.examLevelTable}>
              <MUIDataTable
                columns={generateColumns(t)}
                data={reviewDraftData.selectedSubjects.subjectInfo}
                options={[]}
                components={[]}
              />
            </Grid>
            <Divider />
            <AppealFees {...reviewDraftData.appealFees} />
            <Divider />
            <FormHeader title={t('appeal:ReviewAppeal.Declaration')} />
            <Declaration
              noTitle
              isAgreed={isDeclared}
              handleAgreement={handleAgreement}
              customContent={t('appeal->>AppealReview.AppealDeclarationStatement')}
            />
            {errMsg && (
              <Grid xs={12} md={8}>
                <Typography className={classes.errorText} display="inline" variant="body2">
                  {t('components->>Declaration.Please confirm declaration before you proceed.')}
                </Typography>
              </Grid>
            )}
          </ContentTemplate>

          <ContentTemplate continuous>
            <Grid
              className={classes.buttonContainer}
              container
              direction="row"
              alignItems="flex-start"
              spacing={3}
            >
              <Grid xs={12} md={3}>
                <Button
                  id="button-selectSubject-back"
                  fullWidth
                  className={classes.button}
                  variant="outlined"
                  onClick={() => {
                    onBack();
                  }}
                >
                  {t('appeal:ReviewAppeal.Back')}
                </Button>
              </Grid>
              <Grid xs={12} md={4}>
                <PaymentButton
                  buttonClassName={classes.paymentButton}
                  isUniqueLink={false}
                  disabled={isPaymentDisabled}
                  onPaymentComplete={(isRetry) => {
                    setIsPaymentDisabled(isRetry);
                  }}
                  paymentMethodsAvailable={paymentAccessDetailsOption}
                  onClickPaymentButton={(callback) => {
                    handleClickPaymentButton(callback);
                  }}
                  onHandlePaymentMethod={(callback, method) => {
                    handlePaymentOption(callback, method);
                  }}
                  onSuccessPayment={(txnId) =>
                    history.push(
                      `/${appeal}/${applications.route}/${receipt.route}?internalTransactionRef=${txnId}`
                    )
                  }
                  fullWidth
                  buttonText={t('appeal:ReviewAppeal.Proceed to Make Payment')}
                  buttonErrorText={t('appeal->>AppealReview.BackToAppealApplications')}
                  buttonVariant="contained"
                  type={ApplicationType.APPEAL}
                />
              </Grid>
            </Grid>
          </ContentTemplate>
        </Box>
      )}
    </>
  );
};

export default AppealReview;
